import { useCallback, useMemo, useRef } from 'react';
import { Box, Popover, SpaceBetween, StatusIndicator } from '@cloudscape-design/components';
import { formatSourceName } from '../common/formatting/sourceFormat';
import type { ReviewBasic } from '../common/types';

const VIOLATION_TYPE_LABELS: Record<string, string> = {
  fake_engagement: 'Fake engagement',
  harassment: 'Harassment',
  hate_speech: 'Hate speech',
  offensive_content: 'Offensive content',
  personal_information: 'Personal information',
  off_topic: 'Off-topic',
  spam: 'Spam',
  obscenity: 'Obscenity',
  conflict_of_interest: 'Conflict of interest',
  privacy_violation: 'Privacy violation',
  hearsay: 'Hearsay',
  ai_generated: 'AI-generated',
};

type ViolationCount = {
  type: string;
  label: string;
  count: number;
};

type SourceViolationSummary = {
  source: string;
  sourceName: string;
  totalCount: number;
  byType: ViolationCount[];
};

export type ViolationsSummaryBadgeProps = {
  reviews: ReviewBasic[];
  periodStartDate: string;
  periodEndDate: string;
  source: string;
};

function SourceViolationPopover({ summary }: { summary: SourceViolationSummary }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hoverOpenedRef = useRef(false);

  const getTrigger = useCallback(
    () =>
      wrapperRef.current?.querySelector('[class*="awsui_trigger_"]') as HTMLElement | null,
    [],
  );

  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(() => {
      if (hoverOpenedRef.current) {
        const trigger = getTrigger();
        if (trigger) {
          trigger.dispatchEvent(new KeyboardEvent('keydown', { keyCode: 27, bubbles: true }));
        }
        hoverOpenedRef.current = false;
      }
    }, 300);
  }, [getTrigger]);

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
  }, []);

  const attachBodyListeners = useCallback(() => {
    requestAnimationFrame(() => {
      const bodies = document.querySelectorAll('[class*="awsui_container_"][class*="awsui_refresh_"]');
      const body = bodies[bodies.length - 1];
      if (body) {
        body.addEventListener('mouseenter', cancelClose);
        body.addEventListener('mouseleave', scheduleClose);
      }
    });
  }, [cancelClose, scheduleClose]);

  const handleMouseEnter = () => {
    cancelClose();
    hoverOpenedRef.current = true;
    const trigger = getTrigger();
    if (trigger) {
      trigger.click();
      attachBodyListeners();
    }
  };

  const handleMouseLeave = () => {
    scheduleClose();
  };

  const label = summary.totalCount === 1
    ? `${summary.totalCount} ${summary.sourceName} Policy Violation`
    : `${summary.totalCount} ${summary.sourceName} Policy Violations`;

  return (
    <div
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Popover
        header={`${summary.sourceName} Policy Violations`}
        content={
          <SpaceBetween direction="vertical" size="xxs">
            {summary.byType.map((v) => (
              <Box key={v.type}>
                <Box variant="span" fontWeight="bold">{v.label}</Box>
                {': '}
                <Box variant="span">
                  {v.count} {v.count === 1 ? 'review' : 'reviews'}
                </Box>
              </Box>
            ))}
          </SpaceBetween>
        }
        triggerType="text"
        size="large"
        renderWithPortal
      >
        <StatusIndicator type="warning">{label}</StatusIndicator>
      </Popover>
    </div>
  );
}

export function ViolationsSummaryBadge({ reviews, periodStartDate, periodEndDate, source }: ViolationsSummaryBadgeProps) {
  const summary = useMemo(() => {
    if (source !== 'google' && source !== 'yelp') return null;

    const typeCountMap = new Map<string, number>();

    for (const review of reviews) {
      if (review.source !== source) continue;
      if (!review.review_date || review.review_date < periodStartDate || review.review_date > periodEndDate) continue;
      const violations = review.violations ?? [];
      for (const v of violations) {
        typeCountMap.set(v.violation_type, (typeCountMap.get(v.violation_type) ?? 0) + 1);
      }
    }

    const totalCount = Array.from(typeCountMap.values()).reduce((a, b) => a + b, 0);
    if (totalCount === 0) return null;

    const byType: ViolationCount[] = Array.from(typeCountMap.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({
        type,
        label: VIOLATION_TYPE_LABELS[type] ?? type,
        count,
      }));

    return {
      source,
      sourceName: formatSourceName(source),
      totalCount,
      byType,
    } satisfies SourceViolationSummary;
  }, [reviews, periodStartDate, periodEndDate, source]);

  if (!summary) return null;

  return <SourceViolationPopover summary={summary} />;
}
